import { useContext, useEffect, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { Link, useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../contexts/UserContext";
import { db, storage } from "../../services/firebase";
import {
  createIncidentDraft,
  handoffInjuryIncident,
  listIncidentManagers,
  registerIncidentAttachment,
  removeIncidentAttachment,
  saveIncidentSection,
  submitIncident,
} from "../../services/incidents";
import SignaturePad from "../driver/driverTimesheet/SignaturePad";
import {
  IMMEDIATE_ACTIONS,
  INCIDENT_STEPS,
  INCIDENT_TYPES,
  LOCATION_TYPES,
  STATUS_LABELS,
  incidentTypeLabel,
  isInjuryHandoff,
} from "./incidentConstants";
import { firstIncompleteSection, validateIncidentSection } from "./incidentValidation";

const EMPTY_PART_A = {
  section1: {
    selectedManagerUid: "",
    occurredDate: "",
    occurredTime: "",
    locationType: "",
    address: "",
  },
  section2: { incidentType: "", typeDetails: {} },
  section3: { description: "" },
  section4: { selected: [], otherText: "" },
  section5: { witnessesPresent: null, witnesses: [] },
};

function mergePartA(value = {}) {
  return Object.fromEntries(
    Object.entries(EMPTY_PART_A).map(([key, defaults]) => [key, { ...defaults, ...(value[key] || {}) }]),
  );
}

export default function DriverIncidentEditor() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useContext(UserContext);
  const [incident, setIncident] = useState(null);
  const [partA, setPartA] = useState(EMPTY_PART_A);
  const [step, setStep] = useState(1);
  const [managers, setManagers] = useState([]);
  const [signature, setSignature] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    let active = true;
    listIncidentManagers()
      .then((result) => active && setManagers(result.managers || []))
      .catch((error) => {
        console.error("Failed to load incident managers:", error);
        if (active) setMessage("Manager list could not be loaded. Please try again.");
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (incidentId) return undefined;
    let active = true;
    setBusy(true);
    createIncidentDraft()
      .then((result) => {
        if (active) navigate(`/driver/incidents/${result.incidentId}`, { replace: true });
      })
      .catch((error) => {
        console.error("Failed to create incident draft:", error);
        if (active) {
          setMessage(error?.message || "Could not start the incident report.");
          setLoading(false);
        }
      })
      .finally(() => active && setBusy(false));
    return () => {
      active = false;
    };
  }, [incidentId, navigate]);

  useEffect(() => {
    if (!incidentId) return undefined;
    return onSnapshot(
      doc(db, "incidents", incidentId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setMessage("Incident report not found.");
          setLoading(false);
          return;
        }
        const data = { id: snapshot.id, ...snapshot.data() };
        setIncident(data);
        const nextPartA = mergePartA(data.partA);
        setPartA(nextPartA);
        setStep(data.workflowStatus === "draft_part_a" ? firstIncompleteSection(data) : 1);
        setLoading(false);
      },
      (error) => {
        console.error("Failed to load incident:", error);
        setMessage("Could not load this incident report.");
        setLoading(false);
      },
    );
  }, [incidentId]);

  useEffect(() => {
    if (!incidentId) return undefined;
    return onSnapshot(
      collection(db, "incidents", incidentId, "attachments"),
      (snapshot) => setAttachments(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => (b.uploadedAt?.toMillis?.() || 0) - (a.uploadedAt?.toMillis?.() || 0))),
      (error) => console.error("Failed to load incident attachments:", error),
    );
  }, [incidentId]);

  const editable = incident?.workflowStatus === "draft_part_a" && incident?.reporter?.uid === userInfo?.uid;
  const injuryStop = isInjuryHandoff(partA.section2);

  const updateSection = (number, patch) => {
    setPartA((current) => ({
      ...current,
      [`section${number}`]: { ...current[`section${number}`], ...patch },
    }));
    setErrors({});
    setMessage("");
  };

  async function saveCurrent({ advance = false } = {}) {
    const data = partA[`section${step}`];
    const nextErrors = step <= 5 ? validateIncidentSection(step, data) : {};
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setMessage("Please complete the highlighted fields.");
      return false;
    }
    setBusy(true);
    setMessage("");
    try {
      await saveIncidentSection({ incidentId, section: step, data, advance });
      setMessage("Section saved.");
      if (advance && step < 6 && !(step === 2 && injuryStop)) setStep(step + 1);
      return true;
    } catch (error) {
      console.error("Failed to save incident section:", error);
      setMessage(error?.message || "Could not save this section.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handoffInjury() {
    const saved = await saveCurrent();
    if (!saved) return;
    setBusy(true);
    try {
      const result = await handoffInjuryIncident({ incidentId, acknowledged: true });
      setMessage(`${result.incidentNumber} was saved. Contact your manager immediately.`);
    } catch (error) {
      console.error("Failed to hand off injury incident:", error);
      setMessage(error?.message || "Could not save and close the injury report.");
    } finally {
      setBusy(false);
    }
  }

  async function saveAndExit() {
    if (step <= 5) {
      const saved = await saveCurrent();
      if (!saved) return;
    }
    navigate("/driver/incidents");
  }

  async function submitReport() {
    const incomplete = firstIncompleteSection({ partA });
    if (incomplete < 6) {
      setStep(incomplete);
      setMessage("Complete all required Part A sections before submitting.");
      return;
    }
    if (!signature) {
      setMessage("Sign the report before submitting.");
      return;
    }
    setBusy(true);
    try {
      const result = await submitIncident({ incidentId, signatureDataUrl: signature });
      setMessage(`${result.incidentNumber} was submitted to the selected manager.`);
    } catch (error) {
      console.error("Failed to submit incident:", error);
      setMessage(error?.message || "Could not submit the incident report.");
    } finally {
      setBusy(false);
    }
  }

  async function uploadAttachments(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setMessage("");
    for (const file of files) {
      if (file.size > 15 * 1024 * 1024) {
        setMessage(`${file.name} is larger than 15 MB and was not uploaded.`);
        continue;
      }
      const contentType = attachmentContentType(file);
      if (!contentType) {
        setMessage(`${file.name} is not a supported photo, PDF, or Word document.`);
        continue;
      }
      const attachmentId = doc(collection(db, "incidents", incidentId, "attachments")).id;
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_").slice(-180) || "attachment";
      const storagePath = `incidentAttachments/${incidentId}/${userInfo.uid}/${attachmentId}/${safeName}`;
      const fileRef = ref(storage, storagePath);
      setUploadProgress((old) => ({ ...old, [attachmentId]: { name: file.name, percent: 0 } }));
      try {
        const task = uploadBytesResumable(fileRef, file, { contentType });
        await new Promise((resolve, reject) => task.on("state_changed", (snapshot) => {
          const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setUploadProgress((old) => ({ ...old, [attachmentId]: { name: file.name, percent } }));
        }, reject, resolve));
        const downloadURL = await getDownloadURL(fileRef);
        await registerIncidentAttachment({ incidentId, attachmentId, fileName: file.name, storagePath, contentType, sizeBytes: file.size, downloadURL });
      } catch (error) {
        console.error("Failed to upload incident attachment:", error);
        await deleteObject(fileRef).catch(() => {});
        setMessage(error?.message || `Could not upload ${file.name}.`);
      } finally {
        setUploadProgress((old) => { const next = { ...old }; delete next[attachmentId]; return next; });
      }
    }
  }

  async function removeAttachment(attachmentId) {
    setBusy(true);
    setMessage("");
    try {
      await removeIncidentAttachment({ incidentId, attachmentId });
    } catch (error) {
      console.error("Failed to remove incident attachment:", error);
      setMessage(error?.message || "Could not remove this attachment.");
    } finally {
      setBusy(false);
    }
  }

  if (loading || (!incidentId && busy)) return <PageMessage>Starting incident report...</PageMessage>;

  if (!incident) {
    return <PageMessage tone="error">{message || "Incident report unavailable."}</PageMessage>;
  }

  if (!editable) {
    return (
      <main className="w-full px-4 py-5 sm:px-6 xl:px-8">
        <div className="mx-auto max-w-4xl space-y-4">
          <Link to="/driver/incidents" className="text-sm font-semibold text-emerald-700">← Back to incident reports</Link>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">{incident.incidentNumber || "Incident"}</p>
            <h1 className="mt-2 text-2xl font-semibold text-gray-950">{STATUS_LABELS[incident.workflowStatus] || incident.workflowStatus}</h1>
            <p className="mt-3 text-gray-600">
              {incident.workflowStatus === "injury_handoff"
                ? "This report has been sent to your selected manager. Contact them immediately and wait for further instructions."
                : "This report is now read-only on the driver side and is available to management for review."}
            </p>
            <Summary incident={incident} managers={managers} attachments={attachments} />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full px-4 py-5 sm:px-6 xl:px-8">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={saveAndExit} disabled={busy} className="text-sm font-semibold text-emerald-700 disabled:opacity-50">← Save and return later</button>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">Draft</span>
        </div>

        <header className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">Part A · Driver report</p>
          <h1 className="mt-1 text-2xl font-semibold text-gray-950">Report an incident</h1>
          <p className="mt-1 text-sm text-gray-600">Complete one section at a time. Your selected manager will receive the report.</p>
        </header>

        <IncidentStepper step={step} onSelect={setStep} />

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-5">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Section {step} of 6</p>
            <h2 className="mt-1 text-xl font-semibold text-gray-950">{INCIDENT_STEPS[step - 1]}</h2>
          </div>

          {step === 1 && <SectionOne data={partA.section1} update={(patch) => updateSection(1, patch)} managers={managers} errors={errors} userInfo={userInfo} />}
          {step === 2 && <SectionTwo data={partA.section2} update={(patch) => updateSection(2, patch)} errors={errors} />}
          {step === 3 && <SectionThree data={partA.section3} update={(patch) => updateSection(3, patch)} errors={errors} />}
          {step === 4 && <SectionFour data={partA.section4} update={(patch) => updateSection(4, patch)} errors={errors} />}
          {step === 5 && <SectionFive data={partA.section5} update={(patch) => updateSection(5, patch)} errors={errors} attachments={attachments} uploadProgress={uploadProgress} onUpload={uploadAttachments} onRemove={removeAttachment} busy={busy} />}
          {step === 6 && <SectionSix incident={{ ...incident, partA }} managers={managers} signature={signature} setSignature={setSignature} attachments={attachments} />}

          {message && <div className="mt-5 rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-700">{message}</div>}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-5">
            <button type="button" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1 || busy} className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-40">Back</button>
            <div className="flex flex-wrap gap-2">
              {step < 6 && <button type="button" onClick={() => saveCurrent()} disabled={busy} className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-50">Save</button>}
              {step === 2 && injuryStop ? (
                <button type="button" onClick={handoffInjury} disabled={busy} className="rounded-xl bg-red-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">Save &amp; Close</button>
              ) : step < 6 ? (
                <button type="button" onClick={() => saveCurrent({ advance: true })} disabled={busy} className="rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">Save &amp; continue</button>
              ) : (
                <button type="button" onClick={submitReport} disabled={busy} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">Submit incident</button>
              )}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function IncidentStepper({ step, onSelect }) {
  return (
    <ol className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {INCIDENT_STEPS.map((label, index) => {
        const number = index + 1;
        return (
          <li key={label}>
            <button type="button" onClick={() => onSelect(number)} className={`h-full w-full rounded-xl border px-3 py-3 text-left text-xs font-semibold ${step === number ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-gray-200 bg-white text-gray-600"}`}>
              <span className="block text-[10px] uppercase tracking-wide">Step {number}</span>
              <span className="mt-1 block">{label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function SectionOne({ data, update, managers, errors, userInfo }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <ReadOnlyField label="Reporter" value={userInfo?.displayName || userInfo?.email} />
      <ReadOnlyField label="Position" value={userInfo?.position || "Not recorded"} />
      <Field label="Reporting manager" error={errors.selectedManagerUid} wide>
        <select value={data.selectedManagerUid} onChange={(e) => update({ selectedManagerUid: e.target.value })} className={inputClass(errors.selectedManagerUid)}>
          <option value="">Select manager</option>
          {managers.map((manager) => <option key={manager.uid} value={manager.uid}>{manager.name || manager.email}</option>)}
        </select>
      </Field>
      <Field label="Date of incident" error={errors.occurredDate}><input type="date" value={data.occurredDate} onChange={(e) => update({ occurredDate: e.target.value })} className={inputClass(errors.occurredDate)} /></Field>
      <Field label="Time of incident" error={errors.occurredTime}><input type="time" value={data.occurredTime} onChange={(e) => update({ occurredTime: e.target.value })} className={inputClass(errors.occurredTime)} /></Field>
      <Field label="Location type" error={errors.locationType}><select value={data.locationType} onChange={(e) => update({ locationType: e.target.value })} className={inputClass(errors.locationType)}><option value="">Select location</option>{LOCATION_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></Field>
      <Field label="Incident address or location" error={errors.address} wide><input value={data.address} onChange={(e) => update({ address: e.target.value })} className={inputClass(errors.address)} placeholder="Enter the exact location" /></Field>
    </div>
  );
}

function SectionTwo({ data, update, errors }) {
  const setDetails = (group, patch) => update({ typeDetails: { ...(data.typeDetails || {}), [group]: { ...(data.typeDetails?.[group] || {}), ...patch } } });
  const vehicle = data.typeDetails?.vehicle || {};
  const injury = data.typeDetails?.injury || {};
  const environmental = data.typeDetails?.environmental || {};
  return (
    <div className="space-y-5">
      <Field label="What type of incident occurred?" error={errors.incidentType}>
        <select value={data.incidentType} onChange={(e) => update({ incidentType: e.target.value, typeDetails: {} })} className={inputClass(errors.incidentType)}><option value="">Select incident type</option>{INCIDENT_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
      </Field>
      {data.incidentType === "vehicle_accident" && (
        <div className="grid gap-4 rounded-xl border border-gray-200 p-4 sm:grid-cols-2">
          <h3 className="font-semibold text-gray-900 sm:col-span-2">Vehicle accident details</h3>
          <Field label="Greenspace vehicle registration" error={errors.greenspaceRegistration}><input value={vehicle.greenspaceRegistration || ""} onChange={(e) => setDetails("vehicle", { greenspaceRegistration: e.target.value })} className={inputClass(errors.greenspaceRegistration)} /></Field>
          <YesNo label="Was another vehicle involved?" value={vehicle.otherVehicleInvolved} onChange={(value) => setDetails("vehicle", { otherVehicleInvolved: value })} />
          {vehicle.otherVehicleInvolved === true && <><Field label="Other driver/name"><input value={vehicle.otherDriverName || ""} onChange={(e) => setDetails("vehicle", { otherDriverName: e.target.value })} className={inputClass()} /></Field><Field label="Other vehicle registration"><input value={vehicle.otherRegistration || ""} onChange={(e) => setDetails("vehicle", { otherRegistration: e.target.value })} className={inputClass()} /></Field><Field label="Contact details" wide><input value={vehicle.otherContact || ""} onChange={(e) => setDetails("vehicle", { otherContact: e.target.value })} className={inputClass()} /></Field></>}
          <YesNo label="Was anything damaged?" value={vehicle.anythingDamaged} onChange={(value) => setDetails("vehicle", { anythingDamaged: value })} />
          {vehicle.anythingDamaged === true && <Field label="Briefly describe the damage" wide><textarea value={vehicle.damageDescription || ""} onChange={(e) => setDetails("vehicle", { damageDescription: e.target.value })} className={`${inputClass()} min-h-24`} /></Field>}
          <YesNo label="Were police contacted?" value={vehicle.policeContacted} onChange={(value) => setDetails("vehicle", { policeContacted: value })} />
        </div>
      )}
      {data.incidentType === "injury_illness" && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <YesNo label="Was someone injured or affected?" value={injury.someoneInjuredOrAffected} onChange={(value) => setDetails("injury", { someoneInjuredOrAffected: value })} error={errors.someoneInjuredOrAffected} />
          {injury.someoneInjuredOrAffected === true && <p className="mt-4 text-sm font-semibold text-red-900">Contact your manager immediately. Save and close this report; your selected manager will complete the remaining information.</p>}
        </div>
      )}
      {data.incidentType === "environmental_spill_leak" && (
        <div className="grid gap-4 rounded-xl border border-gray-200 p-4 sm:grid-cols-2">
          <h3 className="font-semibold text-gray-900 sm:col-span-2">Environmental incident details</h3>
          <Field label="What occurred?" error={errors.environmentalEventType}><select value={environmental.eventType || ""} onChange={(e) => setDetails("environmental", { eventType: e.target.value })} className={inputClass(errors.environmentalEventType)}><option value="">Select</option><option value="spill_leak">Spill / leak</option><option value="smoke_dust">Smoke / dust</option><option value="waste_contamination">Waste contamination</option><option value="wildlife_affected">Wildlife affected</option><option value="other">Other</option></select></Field>
          <Field label="Substance or material"><input value={environmental.substance || ""} onChange={(e) => setDetails("environmental", { substance: e.target.value })} className={inputClass()} /></Field>
          <Field label="Was it contained?"><select value={environmental.contained || ""} onChange={(e) => setDetails("environmental", { contained: e.target.value })} className={inputClass()}><option value="">Select</option><option value="yes">Yes</option><option value="no">No</option><option value="not_applicable">N/A</option></select></Field>
          <Field label="Environmental impact" wide><textarea value={environmental.impactDescription || ""} onChange={(e) => setDetails("environmental", { impactDescription: e.target.value })} className={`${inputClass()} min-h-24`} /></Field>
        </div>
      )}
    </div>
  );
}

function SectionThree({ data, update, errors }) {
  return <Field label="Describe what happened in your own words" error={errors.description}><p className="mb-2 text-sm text-gray-500">Explain what you were doing, what happened, and any vehicle, equipment or substance involved.</p><textarea value={data.description} onChange={(e) => update({ description: e.target.value })} className={`${inputClass(errors.description)} min-h-48`} /></Field>;
}

function SectionFour({ data, update, errors }) {
  const toggle = (value) => update({ selected: data.selected.includes(value) ? data.selected.filter((item) => item !== value) : [...data.selected, value] });
  return <div><p className="mb-3 text-sm font-medium text-gray-800">What actions were taken immediately?</p><div className="grid gap-2 sm:grid-cols-2">{IMMEDIATE_ACTIONS.map(([value, label]) => <label key={value} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 text-sm"><input type="checkbox" checked={data.selected.includes(value)} onChange={() => toggle(value)} className="h-4 w-4" />{label}</label>)}</div>{errors.selected && <p className="mt-2 text-xs text-red-700">{errors.selected}</p>}{data.selected.includes("other") && <Field label="Other action"><input value={data.otherText} onChange={(e) => update({ otherText: e.target.value })} className={inputClass()} /></Field>}</div>;
}

function SectionFive({ data, update, errors, attachments, uploadProgress, onUpload, onRemove, busy }) {
  const witnesses = data.witnesses || [];
  const addWitness = () => update({ witnesses: [...witnesses, { name: "", phone: "", email: "" }] });
  const updateWitness = (index, patch) => update({ witnesses: witnesses.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item) });
  const uploading = Object.entries(uploadProgress);
  return <div className="space-y-5">
    <YesNo label="Were there any witnesses?" value={data.witnessesPresent} onChange={(value) => update({ witnessesPresent: value, witnesses: value ? witnesses : [] })} error={errors.witnessesPresent} />
    {data.witnessesPresent === true && <div className="space-y-3">{witnesses.map((witness, index) => <div key={index} className="grid gap-3 rounded-xl border border-gray-200 p-4 sm:grid-cols-3"><Field label="Name"><input value={witness.name} onChange={(e) => updateWitness(index, { name: e.target.value })} className={inputClass()} /></Field><Field label="Contact number"><input value={witness.phone} onChange={(e) => updateWitness(index, { phone: e.target.value })} className={inputClass()} /></Field><Field label="Email"><input type="email" value={witness.email} onChange={(e) => updateWitness(index, { email: e.target.value })} className={inputClass()} /></Field></div>)}<button type="button" onClick={addWitness} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold">+ Add witness</button></div>}
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <h3 className="font-semibold text-gray-900">Photos and supporting evidence</h3>
      <p className="mt-1 text-sm text-gray-600">Attach multiple photos, PDFs, or Word documents. Maximum 15 MB per file.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <label className="cursor-pointer rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white">
          Take photo<input type="file" accept="image/*" capture="environment" className="sr-only" disabled={busy} onChange={(event) => { onUpload(event.target.files); event.target.value = ""; }} />
        </label>
        <label className="cursor-pointer rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800">
          Upload files<input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.pdf,.doc,.docx" multiple className="sr-only" disabled={busy} onChange={(event) => { onUpload(event.target.files); event.target.value = ""; }} />
        </label>
      </div>
      {uploading.length > 0 && <div className="mt-4 space-y-2">{uploading.map(([id, item]) => <div key={id}><div className="flex justify-between text-xs text-gray-600"><span className="truncate">Uploading {item.name}</span><span>{item.percent}%</span></div><div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-200"><div className="h-full rounded-full bg-emerald-600 transition-all" style={{ width: `${item.percent}%` }} /></div></div>)}</div>}
      {attachments.length > 0 && <div className="mt-4 space-y-2">{attachments.map((attachment) => <div key={attachment.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3"><a href={attachment.downloadURL} target="_blank" rel="noreferrer" className="min-w-0 text-sm text-emerald-800 no-underline hover:underline"><span className="block truncate font-semibold">{attachment.fileName}</span><span className="text-xs text-gray-500">{attachment.category === "photo" ? "Photo" : "Document"} · {formatBytes(attachment.sizeBytes)}</span></a><button type="button" disabled={busy} onClick={() => onRemove(attachment.id)} className="shrink-0 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50">Remove</button></div>)}</div>}
    </div>
  </div>;
}

function SectionSix({ incident, managers, signature, setSignature, attachments }) {
  return <div className="space-y-5"><Summary incident={incident} managers={managers} attachments={attachments} /><div><h3 className="mb-2 font-semibold text-gray-900">Reporting-driver signature</h3><SignaturePad value={signature} onChange={setSignature} helpText="Sign above to confirm this incident report is accurate." /></div></div>;
}

function Summary({ incident, managers, attachments = [] }) {
  const partA = mergePartA(incident.partA);
  const selectedManager = managers.find((item) => item.uid === partA.section1.selectedManagerUid) || incident.assignment || {};
  return <div className="mt-5 rounded-xl bg-gray-50 p-4"><dl className="grid gap-4 sm:grid-cols-2"><SummaryItem label="Manager" value={selectedManager.name || selectedManager.selectedManagerName || selectedManager.email || selectedManager.selectedManagerEmail || "Not selected"} /><SummaryItem label="Incident date" value={partA.section1.occurredDate || "Not entered"} /><SummaryItem label="Location" value={partA.section1.address || "Not entered"} /><SummaryItem label="Incident type" value={incidentTypeLabel(partA.section2.incidentType)} /><SummaryItem label="Description" value={partA.section3.description || "Not entered"} wide /></dl>{attachments.length > 0 && <div className="mt-4 border-t border-gray-200 pt-4"><h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Attachments</h3><div className="mt-2 space-y-2">{attachments.map((attachment) => <a key={attachment.id} href={attachment.downloadURL} target="_blank" rel="noreferrer" className="block truncate text-sm font-semibold text-emerald-800 hover:underline">{attachment.fileName} <span className="font-normal text-gray-500">({formatBytes(attachment.sizeBytes)})</span></a>)}</div></div>}</div>;
}

function SummaryItem({ label, value, wide }) { return <div className={wide ? "sm:col-span-2" : ""}><dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt><dd className="mt-1 text-sm text-gray-900">{value}</dd></div>; }
function ReadOnlyField({ label, value }) { return <div><div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div><div className="mt-1 rounded-xl bg-gray-100 px-3 py-2.5 text-sm text-gray-800">{value || "Not recorded"}</div></div>; }
function Field({ label, error, children, wide }) { return <label className={`block ${wide ? "sm:col-span-2" : ""}`}><span className="mb-1 block text-sm font-semibold text-gray-800">{label}</span>{children}{error && <span className="mt-1 block text-xs text-red-700">{error}</span>}</label>; }
function YesNo({ label, value, onChange, error }) { return <fieldset><legend className="mb-2 text-sm font-semibold text-gray-800">{label}</legend><div className="flex gap-4">{[true, false].map((choice) => <label key={String(choice)} className="flex items-center gap-2 text-sm"><input type="radio" checked={value === choice} onChange={() => onChange(choice)} />{choice ? "Yes" : "No"}</label>)}</div>{error && <p className="mt-1 text-xs text-red-700">{error}</p>}</fieldset>; }
function inputClass(error) { return `w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-4 ${error ? "border-red-400 focus:ring-red-100" : "border-gray-300 focus:border-emerald-500 focus:ring-emerald-100"}`; }
function PageMessage({ children, tone = "default" }) { return <main className="w-full px-4 py-8 sm:px-6"><div className={`mx-auto max-w-3xl rounded-2xl border p-6 text-sm ${tone === "error" ? "border-red-200 bg-red-50 text-red-800" : "border-gray-200 bg-white text-gray-700"}`}>{children}</div></main>; }
function formatBytes(value) { const bytes = Number(value || 0); if (bytes < 1024) return `${bytes} B`; if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`; return `${(bytes / (1024 * 1024)).toFixed(1)} MB`; }
function attachmentContentType(file) { const type = String(file?.type || "").toLowerCase(); if (["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(type)) return type; const name = String(file?.name || "").toLowerCase(); if (/\.heic$/.test(name)) return "image/heic"; if (/\.heif$/.test(name)) return "image/heif"; if (/\.docx$/.test(name)) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document"; if (/\.doc$/.test(name)) return "application/msword"; return ""; }
